import isNumber from 'lodash/isNumber';
import get from 'lodash/get';
import moment from 'moment';
import { Parser } from 'expr-eval';
import { round } from 'lodash';

export const incrementSequenceNumber = (input = '') => {
	// Find the last numeric sequence in the string
	const code = input || '';
	const match = code.match(/(\d+)(?!.*\d)/);

	if (!match) {
	// No number found → start numbering from 1
		return `${code}1`;
	}

	const oldNumber = match[1];
	const startIndex = match.index;

	// Increment and preserve leading zeros
	const newNumber = (parseInt(oldNumber, 10) + 1)
		.toString()
		.padStart(oldNumber.length, '0');

	// Replace only the last number part
	return (
		code.slice(0, startIndex) +
		newNumber +
		code.slice(startIndex + oldNumber.length)
	);
};

export const getIsRequired = ({ userSelf, objectType, fieldKey }) => {
	const requiredFields = get(userSelf, `settings.required_${objectType}_fields`, []) || [];
	return requiredFields.includes(fieldKey);
};

export const getAircraftStatusData = (status) => {
	switch (status) {
	case 'in_flight':
		return {
			text: 'IN FLIGHT',
			color: '#87d068'
		};
	case 'in_maintenance':
		return {
			text: 'IN MAINTENANCE',
			color: '#2db7f5'
		};
	case 'maintenance_near_due':
		return {
			text: 'NEAR DUE',
			color: '#fa8c16'
		};
	case 'maintenance_in_tolerance':
		return {
			text: 'IN TOLERANCE',
			color: '#f50'
		};
	case 'needs_maintenance':
		return {
			text: 'NEEDS MAINTENANCE',
			color: '#f5222d'
		};
	case 'out_of_order':
		return {
			text: 'OUT OF ORDER',
			color: null
		};
	case 'inactive':
		return {
			text: 'INACTIVE',
			color: null
		};
	default:
		return {};
	}
};

export const serialize = (obj) => {
	const str = [];

	Object.keys(obj).forEach((key) => {
		const value = obj[key];

		if (Array.isArray(value)) {
			if (!value.length) return;

			value.forEach((v) => {
				if (v || typeof v === 'number') {
					str.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
				}
			});

			return;
		}

		if (!Number.isFinite(value) && !value) return;

		str.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
	});

	return str.join('&');
};

export const getDateTimeFormat = userSelf => `${get(userSelf.settings, 'date_format')} ${get(userSelf.settings, 'time_format')}`;

export const getTrackingParameters = userSelf => get(userSelf, 'active_organization.tracking_parameters') || [];
export const isEnabledParameter = (enabledParameters = [], key) => enabledParameters.includes(key);

export const getMinutesFromHours = (hours) => {
	if (!hours) {
		return 0;
	}

	hours = Math.abs(hours);

	const pointValue = (hours - Math.trunc(hours));

	return Number(((Number(pointValue) * 60)).toFixed());
};

export const displayHoursWithMinutes = (hours) => {
	if (!isNumber(hours)) {
		return 0;
	}

	if (isNumber(hours) && !Number(hours)) {
		return '0:00';
	}

	return `${Math.trunc(hours)}:${`${getMinutesFromHours(hours)}`.padStart(2, '0')}`;
};

export const displayParameterValue = (value, field) => {
	if (!isNumber(value)) {
		return '-';
	}

	if (field.type === 'hours') {
		return displayHoursWithMinutes(value);
	}

	return round(value, field.precision);
};

export const getFieldStatusColor = (remaining, nearDue = 0) => {
	if (remaining <= 0) {
		return '#f5222d';
	}

	if (nearDue && remaining <= nearDue) {
		return '#faad14';
	}

	return null;
};

export const isUUID = (str) => {
	const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
	return uuidRegex.test(str);
};

export const convertHoursAndMinutes = (hours, minutes) => {
	if (!Number(minutes) && !Number(hours)) {
		return 0;
	}

	const isNegative = hours < 0;

	hours = Math.abs(Math.trunc(hours) || 0);
	minutes = Number((Number(minutes) / 60).toFixed(4)) || 0;

	const total = hours + minutes;

	return isNegative ? -(total) : total;
};

export const getStatusColor = (status) => {
	switch (status) {
	case 'not_due':
		return '#87d068';
	case 'near_due':
		return '#faad14';
	case 'past_due':
		return '#f5222d';
	case 'projection':
		return '#0800ff';
	case 'inactive':
		return '#777777';
	case 'open':
		return 'red';
	default:
		return '#87d068';
	}
};

export const getWorkOrderStatusData = (status) => {
	switch (status) {
	case 'in_preparation':
		return {
			text: 'IN PREPARATION',
			color: null
		};
	case 'ready_for_work':
		return {
			text: 'READY FOR WORK',
			color: '#108ee9'
		};
	case 'in_progress':
		return {
			text: 'IN PROGRESS',
			color: '#2db7f5'
		};
	case 'done':
		return {
			text: 'COMPLETED',
			color: '#87d068'
		};
	case 'scheduled':
		return {
			text: 'SCHEDULED',
			color: '#722ed1'
		};
	case 'closed':
		return {
			text: 'CLOSED',
			color: '#faad14'
		};
	case 'cancelled':
		return {
			text: 'CANCELLED',
			color: '#777777'
		};
	default:
		return {
			text: 'IN PREPARATION',
			color: null
		};
	}
};

export const getStatusData = (status) => {
	switch (status) {
	case 'active':
		return {
			text: 'ACTIVE',
			color: getStatusColor(status)
		};
	case 'not_due':
		return {
			text: 'NOT DUE',
			color: getStatusColor(status)
		};
	case 'near_due':
		return {
			text: 'NEAR DUE',
			color: getStatusColor(status)
		};
	case 'past_due':
		return {
			text: 'PAST DUE',
			color: getStatusColor(status)
		};
	case 'inactive':
		return {
			text: 'INACTIVE',
			color: getStatusColor(status)
		};
	case 'open':
		return {
			text: 'OPEN',
			color: getStatusColor(status)
		};
	case 'closed':
		return {
			text: 'CLOSED',
			color: 'default'
		};
	default:
		return {
			text: 'NOT DUE',
			color: getStatusColor(status)
		};
	}
};


export const getCustomFields = (userSelf, objectType, appliesTo) => {
	if (!objectType) {
		return Object.values(get(userSelf, 'active_organization.custom_fields', {})).flat();
	}

	const customFields = get(userSelf, `active_organization.custom_fields.${objectType}`, []) || [];

	if (appliesTo) {
		return customFields.filter(field => !field.applies_to || field.applies_to.includes(appliesTo));
	}

	return customFields;
};

export const getActiveWorkDescription = (workEntry) => {
	switch (workEntry.parent_type) {
	case 'work_order_op':
		if (get(workEntry.work_meta, 'work_order_id')) {
			return `${get(workEntry.work_meta, 'registration') ? `${get(workEntry.work_meta, 'registration')} / ` : ''}
				${get(workEntry.work_meta, 'nr')} / ${get(workEntry.work_meta, 'name') || get(workEntry.work_meta, 'op_nr')}`;
		}

		if (get(workEntry.work_meta, 'tech_log_id')) {
			return `${get(workEntry.work_meta, 'registration') ? `${get(workEntry.work_meta, 'registration')} / ` : ''}
				${get(workEntry.work_meta, 'tlog')} / ${get(workEntry.work_meta, 'name') || get(workEntry.work_meta, 'op_nr')}`;
		}
		return '';
	case 'defect':
		if (get(workEntry.work_meta, 'work_order_id')) {
			return `${get(workEntry.work_meta, 'registration') ? `${get(workEntry.work_meta, 'registration')} / ` : ''}
				${get(workEntry.work_meta, 'nr')} / ${get(workEntry.work_meta, 'description')}`;
		}

		if (get(workEntry.work_meta, 'tech_log_id')) {
			return `${get(workEntry.work_meta, 'registration') ? `${get(workEntry.work_meta, 'registration')} / ` : ''}
				${get(workEntry.work_meta, 'tlog')} / ${get(workEntry.work_meta, 'description')}`;
		}
		return '';
	case 'work_order':
		return `${get(workEntry.work_meta, 'registration') ? `${get(workEntry.work_meta, 'registration')} / ` : ''}
			${get(workEntry.work_meta, 'nr')}`;
	case 'tech_log':
		return `${get(workEntry.work_meta, 'registration') ? `${get(workEntry.work_meta, 'registration')} / ` : ''}
			${get(workEntry.work_meta, 'tlog')}`;
	default:
		return get(workEntry, 'other_type_value') || 'Other work';
	}
};

export const getFormattedDuration = (dur, displaySeconds) => {
	const duration = moment.duration(dur, 'milliseconds');
	const hours = (Math.floor(duration.asHours()))
		.toLocaleString(undefined, { minimumIntegerDigits: 2 });
	const mins = (Math.floor(duration.asMinutes()) - (hours * 60))
		.toLocaleString(undefined, { minimumIntegerDigits: 2 });
	const sec = (Math.floor(duration.asSeconds()) - (hours * 60 * 60) - (mins * 60))
		.toLocaleString(undefined, { minimumIntegerDigits: 2 });

	const result = `${hours || '00'}:${mins || '00'}${displaySeconds ? `:${sec || '00'}` : ''}`;

	return result;
};

export const getFormattedTotalTime = (totalMinutes) => {
	let minutes = Math.round((totalMinutes % 60) || 0);
	let hours = Math.round(((totalMinutes - minutes) / 60) || 0);

	if (minutes === 60) {
		hours += 1;
		minutes = 0;
	}

	return `${hours || 0}h ${minutes || 0}min`;
};

export const getLinkedParametersData = ({
	trackingParameters,
	currentValues,
	newValues,
	dataPrefix = ''
}) => {
	// Copy current values
	const values = { ...currentValues };

	// Delta accumulated for each parameter
	const deltas = {};

	// Apply user changes
	for (const [key, newValue] of Object.entries(newValues)) {
		const oldValue = values[key] ?? 0;

		values[key] = newValue;
		deltas[key] = (deltas[key] ?? 0) + (newValue - oldValue);
	}

	// Topological sort
	const inDegree = {};
	const byKey = {};

	for (const parameter of trackingParameters) {
		byKey[parameter.key] = parameter;
		inDegree[parameter.key] ??= 0;
	}

	for (const parameter of trackingParameters) {
		for (const linked of parameter.linked_parameters ?? []) {
			inDegree[linked.key] = (inDegree[linked.key] ?? 0) + 1;
		}
	}

	const queue = [];

	for (const key in inDegree) {
		if (inDegree[key] === 0) {
			queue.push(key);
		}
	}

	while (queue.length) {
		const key = queue.shift();
		const parameter = byKey[key];

		const delta = deltas[key] ?? 0;

		// Don't overwrite directly edited values
		if (!(key in newValues)) {
			values[key] = (values[key] ?? 0) + delta;
		}

		for (const linked of parameter?.linked_parameters ?? []) {
			const propagatedDelta = delta * (linked.multiplier ?? 1);

			deltas[linked.key] =
				(deltas[linked.key] ?? 0) + propagatedDelta;

			inDegree[linked.key]--;

			if (inDegree[linked.key] === 0) {
				queue.push(linked.key);
			}
		}
	}

	if (dataPrefix) {
		const renamed = Object.entries(values).reduce((result, [key, val]) => {
			// eslint-disable-next-line no-param-reassign
			result[`${dataPrefix}${key}`] = val;
			return result;
		}, {});

		return renamed;
	}

	return values;
};

export const calculateParametersFormula = ({
	data,
	fieldPrefix = '',
	trackingParameters = []
}) => {
	const fieldsWithFormula = trackingParameters.filter(p => p.is_calculated);

	const calculations = {};

	fieldsWithFormula.forEach((field) => {
		let { formula } = field;

		field.required_parameters.forEach((key) => {
			const regex = new RegExp(`\\$${key}`, 'g');
			formula = formula.replace(regex, (data[`${fieldPrefix}${key}`] || 0));
		});

		const parsedValue = round(Parser.evaluate(formula), 4);

		calculations[`${fieldPrefix}${field.key}`] = parsedValue;
	});

	return calculations;
};
